import React, { useMemo, useRef, useState, useCallback } from "react";
import { useNewestProducts } from "@/hooks/use-homepage";
import { useDbProductImagesByProductIds, useDbCategories, toCartProduct, useVariantStockSummary } from "@/hooks/use-products";
import ProductCard from "@/components/products/ProductCard";
import GiftCardProductCard from "@/components/products/GiftCardProductCard";
import type { DbProduct } from "@/types/product";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HOMEPAGE_PRODUCTS_LIMIT = 10;

const NewestProducts = () => {
  const { data: newest = [], isLoading } = useNewestProducts(HOMEPAGE_PRODUCTS_LIMIT);
  const newestIds = useMemo(() => (newest as DbProduct[]).map((p) => p.id), [newest]);
  const { data: imagesForVisible = [] } = useDbProductImagesByProductIds(newestIds);
  const { data: categories = [] } = useDbCategories();
  const { data: variantStockMap } = useVariantStockSummary();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const products = useMemo(
    () =>
      (newest as DbProduct[]).map((p) => {
        const imgs = imagesForVisible.filter((i) => i.product_id === p.id);
        return toCartProduct(p, imgs, categories, variantStockMap);
      }),
    [newest, imagesForVisible, categories, variantStockMap]
  );

  // Insert gift card at a random-ish position (seeded per session via product count)
  const giftCardIndex = useMemo(() => {
    if (products.length < 2) return 0;
    return 2 + (products.length % 3); // deterministic but varied position (index 2-4)
  }, [products.length]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector<HTMLElement>("[data-card]")?.offsetWidth ?? 240;
    el.scrollBy({ left: dir === "left" ? -cardWidth : cardWidth, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <section className="container mx-auto px-4 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-8">Jaunie produkti</h2>
        <div className="flex gap-4 overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-muted rounded-xl h-[370px] min-w-[calc((100%-4*1rem)/5)] animate-pulse hidden md:block first:block [&:nth-child(2)]:block" />
          ))}
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-12 md:py-16">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-2xl md:text-3xl font-bold">Jaunie produkti</h2>
        <div className="hidden md:flex gap-2">
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30 disabled:cursor-default transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30 disabled:cursor-default transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex gap-4 md:gap-5 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory md:snap-none"
      >
        {products.map((product, idx) => (
          <React.Fragment key={product.id}>
            {idx === giftCardIndex && (
              <div data-card className="min-w-[calc(100%-2rem)] sm:min-w-[45vw] md:min-w-[calc((100%-4*1.25rem)/5)] md:max-w-[calc((100%-4*1.25rem)/5)] shrink-0 snap-start">
                <GiftCardProductCard />
              </div>
            )}
            <div
              data-card
              className="min-w-[calc(100%-2rem)] sm:min-w-[45vw] md:min-w-[calc((100%-4*1.25rem)/5)] md:max-w-[calc((100%-4*1.25rem)/5)] shrink-0 snap-start"
            >
              <ProductCard product={product} priority={idx < 5} />
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default NewestProducts;
